import { Stack } from '@mui/material'

import { Chip } from '@/components/UI/Chip'
import { ITableColumn } from '@/components/UI/DataTable/TableHead'
import { IRecurringIncome } from '@/types'
import { formatDate } from '@/utils/common'
import { cell } from '../cell'
import { formatDay, formatDollars, formatEnum } from '../format'

export type RecurringIncomeState = 'Active' | 'Ended'

// The API only returns live streams, so a stream is either still paying or has an end date behind
// it. An ended one is still history the decisioning can see.
export const recurringIncomeState = (
  row: Pick<IRecurringIncome, 'endDate'>,
  now: Date = new Date()
): RecurringIncomeState => {
  if (row.endDate && new Date(row.endDate) <= now) return 'Ended'
  return 'Active'
}

const STATE_COLOURS: Record<RecurringIncomeState, string> = {
  Active: '#067647',
  Ended: '#344054',
}

// Weekly and fortnightly streams land on a weekday; monthly ones on a day of the month.
const payDay = (row: IRecurringIncome) => {
  if (row.payDayOfWeek) return formatEnum(row.payDayOfWeek)
  if (typeof row.payDayOfMonth === 'number') return `Day ${row.payDayOfMonth}`
  return ''
}

const credits = (row: IRecurringIncome) => {
  const { recurringCreditCount: seen, expectedRecurringCreditCount: expected } = row
  if (typeof seen !== 'number') return ''
  return typeof expected === 'number' ? `${seen} / ${expected}` : String(seen)
}

export const recurringIncomeColumns: ITableColumn<IRecurringIncome>[] = [
  {
    key: 'description',
    id: 'description',
    label: 'Description',
    minWidth: '220px',
    render: (_v, r) => (
      <Stack direction="row" alignItems="center" gap={1}>
        {cell(r.description)}
        {r.isPrimaryIncome && <Chip value="Primary" chipColor="#4C4CFF" />}
      </Stack>
    ),
  },
  {
    key: 'status',
    id: 'status',
    label: 'Status',
    render: (_v, r) => {
      const state = recurringIncomeState(r)
      return <Chip value={state} chipColor={STATE_COLOURS[state]} />
    },
  },
  {
    key: 'amount',
    id: 'amount',
    label: 'Amount',
    align: 'right',
    render: (_v, r) => cell(formatDollars(r.amount)),
  },
  {
    key: 'payType',
    id: 'payType',
    label: 'Pay Type',
    render: (_v, r) => cell(formatEnum(r.payType)),
  },
  {
    key: 'recurringFrequency',
    id: 'recurringFrequency',
    label: 'Frequency',
    render: (_v, r) => cell(formatEnum(r.recurringFrequency)),
  },
  { key: 'payDay', id: 'payDay', label: 'Pay Day', render: (_v, r) => cell(payDay(r)) },
  {
    key: 'credits',
    id: 'credits',
    label: 'Credits (Seen / Expected)',
    minWidth: '120px',
    render: (_v, r) => cell(credits(r)),
  },
  {
    key: 'payCycleScore',
    id: 'payCycleScore',
    label: 'Pay Cycle Score',
    render: (_v, r) => cell(r.payCycleScore.toFixed(2)),
  },
  {
    key: 'startDate',
    id: 'startDate',
    label: 'Start Date',
    minWidth: '120px',
    render: (_v, r) => cell(formatDay(r.startDate)),
  },
  {
    key: 'suggestedStartDate',
    id: 'suggestedStartDate',
    label: 'Suggested Start',
    minWidth: '120px',
    render: (_v, r) => cell(formatDay(r.suggestedStartDate)),
  },
  {
    key: 'endDate',
    id: 'endDate',
    label: 'End Date',
    minWidth: '120px',
    render: (_v, r) => cell(formatDay(r.endDate)),
  },
  {
    key: 'basiqFinancialAccountId',
    id: 'basiqFinancialAccountId',
    label: 'Financial Account Id',
    render: (_v, r) => cell(r.basiqFinancialAccountId),
  },
  {
    key: 'modelVersion',
    id: 'modelVersion',
    label: 'Model Version',
    render: (_v, r) => cell(r.modelVersion),
  },
  {
    key: 'createdAt',
    id: 'createdAt',
    label: 'Created At',
    minWidth: '170px',
    render: (_v, r) => cell(r.createdAt ? formatDate(r.createdAt, false, true) : ''),
  },
]
